// /frontend/src/pages/Dashboard.jsx
// Fully responsive dashboard with mobile-first design

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ONBOARDING_STEP_ROUTES } from "../utils/onboarding";
import DashboardHeader from "../components/DashboardHeader";
import MetricCards from "../components/MetricCards";
import RetentionCharts from "../components/RetentionCharts";
import TaskList from "../components/TaskList";
import ActivityFeed from "../components/ActivityFeed";
import RiskList from "../components/RiskList";
import AlertsWidget from "../components/AlertsWidget";
import QuickLookup from "../components/QuickLookup";
import SectionLinks from "../components/SectionLinks";
import MorningBriefing from "../components/MorningBriefing";
import AIToolbar from "../components/AIToolbar";
import PriorityCallQueue from "../components/PriorityCallQueue";
import RiskDistributionChart from "../components/RiskDistributionChart";
import CommunicationsHub from "../components/CommunicationsHub";


const SETUP_CARD_DISMISSED_KEY = 'proirp-setup-card-dismissed';

function SetupCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(SETUP_CARD_DISMISSED_KEY) === 'true'
  );

  const hasImported = user?.onboarding?.hasImportedClients;
  const hasReviewed = user?.onboarding?.hasReviewedAutomations;

  // Don't render if both complete, if onboarding data unavailable, or dismissed this session
  if (!user?.onboarding) return null;
  if (hasImported && hasReviewed) return null;
  if (dismissed) return null;

  function dismiss() {
    sessionStorage.setItem(SETUP_CARD_DISMISSED_KEY, 'true');
    setDismissed(true);
  }

  const items = [
    {
      key: 'import_clients',
      done: hasImported,
      label: 'Import your clients',
      sublabel: 'Enables risk scoring, automations, and OEP tracking',
    },
    {
      key: 'review_automations',
      done: hasReviewed,
      label: 'Review your automations',
      sublabel: 'Confirm your default outreach is configured for your practice',
    },
  ];

  return (
    <div className="bg-white border border-amber-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Finish setting up your account</h3>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-600 text-base leading-none"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
      <div className="space-y-2">
        {items.map(({ key, done, label, sublabel }) => (
          <button
            key={key}
            onClick={() => !done && navigate(ONBOARDING_STEP_ROUTES[key])}
            disabled={done}
            className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition ${
              done
                ? 'border-green-200 bg-green-50 cursor-default'
                : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50 cursor-pointer'
            }`}
          >
            <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs ${
              done ? 'border-green-500 bg-green-500 text-white' : 'border-slate-300'
            }`}>
              {done ? '✓' : ''}
            </span>
            <div>
              <div className={`text-sm font-medium ${done ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                {label}
              </div>
              {!done && <div className="text-xs text-slate-500 mt-0.5">{sublabel}</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const Dashboard = () => (
  <div
    className="min-h-screen bg-gray-100 py-4 sm:py-6 lg:py-8 px-0 sm:px-2 lg:px-4"
    style={{ fontFamily: "Inter, sans-serif" }}
  >
    {/* Header - responsive */}
    <div className="px-3 sm:px-0">
      <DashboardHeader />
    </div>

    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-5">
      {/* 1. Daily Brief Snapshot + AI Toolbar */}
      <div className="px-3 sm:px-0 space-y-2">
        <MorningBriefing compact={true} />
        <AIToolbar />
      </div>

      {/* 1b. Setup Card — shown to new users until import + automations complete */}
      <div className="px-3 sm:px-0">
        <SetupCard />
      </div>

      {/* 2. Risk Distribution Chart */}
      <div className="px-3 sm:px-0">
        <RiskDistributionChart />
      </div>

      {/* 3. Conversations - 3-column inbox */}
      <div className="px-3 sm:px-0">
        <CommunicationsHub />
      </div>

      {/* 4. Tasks - Full width */}
      <div className="px-3 sm:px-0">
        <TaskList />
      </div>

      {/* Metric Cards */}
      <div className="px-3 sm:px-0">
        <MetricCards />
      </div>

      {/* Main Grid - stacks on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 px-3 sm:px-0">
        {/* Main column - full width on mobile, 2/3 on desktop */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
          {/* Priority Call Queue */}
          <PriorityCallQueue limit={8} />

          {/* Retention Charts */}
          <RetentionCharts />

          {/* Activity Feed */}
          <ActivityFeed />
        </div>

        {/* Sidebar widgets - full width on mobile, 1/3 on desktop */}
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          {/* At Risk Clients */}
          <RiskList />

          {/* Alerts */}
          <AlertsWidget />

          {/* Quick Lookup */}
          <QuickLookup />

          {/* Section Links */}
          <SectionLinks />
        </div>
      </div>
    </div>

  </div>
);

export default Dashboard;
